import { Add, DriveFileRenameOutline, East } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  MenuItem,
  DialogActions,
  colors,
  useMediaQuery,
  Chip,
} from "@mui/material";
import React, { useMemo, useRef, useState, useCallback } from "react";
import { useQuery, useMutation } from "react-query";
import { queryClient } from "App";
import ExportButtonGroup from "app/components/common/ExportButtonGroup";

import CommonTable1 from "app/components/Tables/CommonTable1";
import DialogHeader from "app/components/common/DialogHeader";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";
import { MenuProps } from "app/utils/helper";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import {
  deleteTransportRoute,
  getAllRouteList,
  getTransport,
  replaceVehicle,
  updateTransportRoute,
} from "app/services/schoolService/transport.service";
import ViewRouteDetail from "app/components/common/Dialogs/transport/ViewRouteDetail";
import AddRouteDialog from "app/components/common/Dialogs/transport/AddRouteDialog";
import ViewOldStopsDialog from "app/components/common/Dialogs/transport/ViewOldStopsDialog";
import StatusChangeDialog from "../../components/StatusChangeDialog";
import { useSelector } from "react-redux";
import { selectActiveSession } from "app/modules/schools/selectors";
import TableActionButtons from "app/components/common/tableComponent/TableActionButtons";
import DeleteDialog from "app/components/ConfirmationDialog/DeleteDialog";
import CustomLinearProgress from "app/components/common/CustomLinearProgress";
import YouTubeFloating from "app/components/common/floatingButton/YouTubeFloating";
import { TransportationRouteDetailLinks } from "app/utils/youTubeLink";
import { Helmet } from "react-helmet";
import UniversalExportDialog from "app/components/common/Dialogs/UniversalExportDialog";

const RouteDetail = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const selectedSession = useSelector(selectActiveSession);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [values, setValues] = useState({
    routeDocId: "",
    routeData: {},
    createDialog: false,
    viewDialog: false,
    viewOldStopDialog: false,
    routeNumber: "",
    routeName: "",
    edit: false,
    statusChangeDialog: false,
    selectedRouteForStatusChange: null,
  });
  const [vehicle, setVehicle] = useState({
    current: "",
    selected: "",
    route: "",
  });
  const [showPdfExportDialog, setShowPdfExportDialog] = useState(false);
  const [showExcelExportDialog, setShowExcelExportDialog] = useState(false);

  // get All Route
  const {
    data: getAllRouteData,
    isLoading: getAllRouteLoading,
    isFetching: getAllRouteFetching,
    refetch: getAllRouteListRefetch,
  } = useQuery({
    queryKey: ["getAllRoute", selectedSession],
    queryFn: () =>
      getAllRouteList({
        session: selectedSession,
        oldData: true,
      }),
    onSuccess: (isSuccess) => {},
  });

  // get All Transport
  const { refetch: getAllTransportRefetch, data: getAllTransportData } =
    useQuery({
      queryKey: ["getTransport"],
      queryFn: () => getTransport({ status: "ACTIVE" }),
    });

  const { mutate: replaceVehicleMutate, isLoading: replaceVehicleLoading } =
    useMutation(replaceVehicle, {
      onSuccess: (res) => {
        toast.success(res?.data?.message);
        queryClient.invalidateQueries({ queryKey: "getAllRoute" });
        setVehicleDialog(false);
        setVehicle((prev) => ({ route: "", selected: "", current: "" }));
      },
      onError: (err) => toast.error(err?.response?.data?.message),
    });
  const {
    mutate: deleteTransportRouteMutate,
    isLoading: deleteTransportRouteLoading,
  } = useMutation(deleteTransportRoute, {
    onSuccess: (res) => {
      toast.success(res?.data?.message);
      queryClient.invalidateQueries({ queryKey: "getAllRoute" });
      setDeleteDialog(false);
      setValues((prev) => ({
        ...prev,
        routeDocId: "",
        routeData: {},
      }));
    },
    onError: (err) => toast.error(err?.response?.data?.message),
  });

  // Update route status mutation
  const {
    mutate: updateRouteStatusMutate,
    isLoading: updateRouteStatusLoading,
  } = useMutation(updateTransportRoute, {
    onSuccess: (res) => {
      toast.success(res?.data?.message || "Route status updated successfully");
      queryClient.invalidateQueries({ queryKey: "getAllRoute" });
      getAllRouteListRefetch();
      // Close the status change dialog
      setValues((prev) => ({
        ...prev,
        statusChangeDialog: false,
        selectedRouteForStatusChange: null,
      }));
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.message || "Failed to update route status",
      ),
  });

  // Handle status change function
  const handleStatusChange = useCallback((routeData) => {
    setValues((prev) => ({
      ...prev,
      statusChangeDialog: true,
      selectedRouteForStatusChange: routeData,
    }));
  }, []);

  const handleConfirmStatusChange = () => {
    const routeData = values.selectedRouteForStatusChange;
    const body = {
      routeDocId: routeData._id,
      session: selectedSession,
      routeCode: routeData.routeCode,
      routeName: routeData.routeName,
      partiallyDeleted: !routeData.partiallyDeleted,
    };

    updateRouteStatusMutate(body);
  };

  // Close status change dialog
  const handleCloseStatusChangeDialog = () => {
    setValues((prev) => ({
      ...prev,
      statusChangeDialog: false,
      selectedRouteForStatusChange: null,
    }));
  };

  const exportHeaders = useMemo(
    () => [
      "Route No",
      "Route Name",
      "Range",
      "Stops",
      "Assigned Vehicle",
      "Status",
    ],
    [],
  );

  const exportData = useMemo(() => {
    return (
      getAllRouteData?.data?.list.map((item) => ({
        "Route No": item?.routeNumber ?? "--",
        "Route Name": item?.routeName ?? "--",
        Range:
          item?.rangeList
            ?.map((range) => `[${range?.fromDistance}-${range?.toDistance}]`)
            .join(",") ?? "--",
        Stops: item?.stopList?.map((name) => name?.stopName).join(",") ?? "--",
        "Assigned Vehicle":
          item?.assignedVehicle
            ?.map(({ vehicleNumber }) =>
              vehicleNumber ? vehicleNumber : "No Vehicle",
            )
            .join(",") ?? "--",
        Status: item?.partiallyDeleted ? "Inactive" : "Active",
      })) || []
    );
  }, [getAllRouteData]);

  const columns = useMemo(() => {
    return [
      {
        header: "Route No",
        accessorKey: "routeNumber",
        maxSize: isMobile ? 20 : 50,
      },
      {
        header: "Route Name",
        accessorKey: "routeName",
        size: isMobile ? 30 : 60,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Typography
            onClick={() =>
              setValues((prev) => ({
                ...prev,
                viewDialog: true,
                routeData: original,
              }))
            }
            sx={{
              color: "#2789FD",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {original?.routeName}
          </Typography>
        ),
      },
      {
        header: "Range",
        accessorKey: "rangeList",
        size: isMobile ? 30 : 60,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Stack direction="row" flexWrap="wrap">
              {original?.rangeList?.map((range, index) => (
                <Typography key={index}>
                  {"["}
                  {range?.fromDistance} {"-"} {range?.toDistance}
                  {"]"}
                  {index + 1 < original?.rangeList?.length && (
                    <>
                      <East
                        sx={{
                          fontSize: isMobile ? 10 : 14,
                          color: colors.blue[500],
                        }}
                      />
                    </>
                  )}
                </Typography>
              ))}
            </Stack>
          );
        },
      },
      {
        header: "Stops",
        accessorKey: "stopList",
        size: isMobile ? 30 : 60,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Stack direction="row" flexWrap="wrap">
              {original?.stopList?.map((stop, index) => (
                <Typography>
                  {stop?.stopName}
                  {index + 1 < original?.stopList?.length && (
                    <>
                      <East
                        sx={{
                          fontSize: isMobile ? 10 : 14,
                          color: colors.blue[500],
                        }}
                      />
                    </>
                  )}
                </Typography>
              ))}
            </Stack>
          );
        },
      },
      {
        header: "Assigned Vehicle",
        size: isMobile ? 60 : 80,
        accessorKey: "assignedVehicle",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {original?.assignedVehicle?.map(({ vehicleNumber }, index) =>
                vehicleNumber ? (
                  <Chip
                    key={index}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <span>{vehicleNumber}</span>
                        <DriveFileRenameOutline fontSize="small" />
                      </Box>
                    }
                    color="success"
                    size="small"
                    onClick={() => {
                      setVehicle((prev) => ({
                        ...prev,
                        route: original?._id,
                        current: vehicleNumber,
                      }));
                      setVehicleDialog(true);
                    }}
                    sx={{
                      cursor: "pointer",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      fontSize: isMobile ? "10px" : "12px",
                      minWidth: isMobile ? "70px" : "100px",
                      "&:hover": {
                        backgroundColor: colors.green[500],
                      },
                    }}
                  />
                ) : (
                  <Chip
                    key={index}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <span>No Vehicle</span>
                        <DriveFileRenameOutline fontSize="small" />
                      </Box>
                    }
                    color="error"
                    size="small"
                    onClick={() => {
                      setVehicle((prev) => ({
                        ...prev,
                        route: original?._id,
                        current: vehicleNumber,
                      }));
                      setVehicleDialog(true);
                    }}
                    sx={{
                      cursor: "pointer",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      fontSize: isMobile ? "10px" : "12px",
                      minWidth: isMobile ? "70px" : "100px",
                      "&:hover": {
                        backgroundColor: colors.red[500],
                      },
                    }}
                  />
                ),
              )}
            </Stack>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const isActive = !!original?.partiallyDeleted;
          return (
            <Chip
              label={isActive ? "Inactive" : "Active"}
              icon={<DriveFileRenameOutline />}
              color={isActive ? "error" : "success"}
              variant="outlined"
              size="medium"
              onClick={() => handleStatusChange(original)}
            />
          );
        },
        maxSize: isMobile ? 20 : 40,
      },
      {
        header: "Old Stops",
        accessorKey: "oldStopList",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Chip
              label={`${original?.oldStopList?.length}`}
              color="primary"
              variant="outlined"
              size="small"
              onClick={() => {
                if (original?.oldStopList?.length) {
                  setValues((prev) => ({
                    ...prev,
                    viewOldStopDialog: true,
                    oldStopListData: original,
                  }));
                }
              }}
              sx={{
                cursor: original?.oldStopList?.length
                  ? "pointer"
                  : "not-allowed",
              }}
            />
          );
        },
        maxSize: isMobile ? 20 : 40,
      },
      {
        header: "Actions",
        Cell: ({ cell }) => {
          return (
            <TableActionButtons
              showView={true}
              showEdit={true}
              showDelete={true}
              handleEdit={() => {
                setValues((prev) => ({
                  ...prev,
                  edit: true,
                  routeData: cell?.row?.original,
                }));
              }}
              handleDelete={() => {
                setDeleteDialog(true);
                setValues((prev) => ({
                  ...prev,
                  routeDocId: cell?.row?.original?._id,
                  routeData: cell?.row?.original,
                }));
              }}
              handleView={() => {
                setValues((prev) => ({
                  ...prev,
                  viewDialog: true,
                  routeData: cell?.row?.original,
                }));
              }}
            />
          );
        },
        size: isMobile ? 20 : 50,
      },
    ];
  }, [isMobile, handleStatusChange]);

  return (
    <>
      <Helmet>
        <title>Route Detail | Transportation</title>
      </Helmet>

      <UniversalExportDialog
        open={showPdfExportDialog}
        onClose={() => setShowPdfExportDialog(false)}
        exportType="pdf"
        pdfHeaders={exportHeaders}
        pdfData={exportData}
        fileName="Route_Detail"
        pdfTitle="Route Detail"
        showSchoolHeader={true}
        showOrientation={true}
        showSelectAll={true}
      />

      {/* Excel Export Dialog */}
      <UniversalExportDialog
        open={showExcelExportDialog}
        onClose={() => setShowExcelExportDialog(false)}
        exportType="excel"
        excelHeaders={exportHeaders}
        excelData={exportData}
        fileName="Route_Detail"
        showSelectAll={true}
        showOrientation={false}
        showSchoolHeader={true}
      />

      <YouTubeFloating videoLink={TransportationRouteDetailLinks} />

      {/* create Dialog */}
      {values.createDialog && (
        <AddRouteDialog
          open={values.createDialog}
          onClose={() =>
            setValues((prev) => ({
              ...prev,
              createDialog: false,
            }))
          }
          refetch={() => getAllRouteListRefetch()}
        />
      )}

      {/* edit dialog */}
      {values.edit && (
        <AddRouteDialog
          open={values.edit}
          routeData={values.routeData}
          onClose={() =>
            setValues((prev) => ({
              ...prev,
              edit: false,
              routeData: {},
            }))
          }
          refetch={() => getAllRouteListRefetch()}
        />
      )}

      <DeleteDialog
        handleClose={() => {
          setDeleteDialog(false);
          setValues((prev) => ({
            ...prev,
            routeDocId: "",
            routeData: {},
          }));
        }}
        handleDelete={() => {
          deleteTransportRouteMutate(values.routeDocId);
        }}
        loading={deleteTransportRouteLoading}
        open={deleteDialog}
        title={`${values.routeData?.routeName} - ${values.routeData?.routeNumber} route`}
      />

      {/* replace vehicle dialog */}
      <Dialog
        onClose={() => {
          setVehicleDialog(false);
          setVehicle((prev) => ({ route: "", selected: "", current: "" }));
        }}
        open={vehicleDialog}
        PaperProps={{
          sx: { minWidth: isMobile ? 300 : 500 },
        }}
      >
        <DialogTitle sx={{ p: "0 !important" }}>
          <DialogHeader
            title="Want to replace ?"
            handleClose={() => {
              setVehicleDialog(false);
              setVehicle((prev) => ({ route: "", selected: "", current: "" }));
            }}
          />
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" gap={2} mt={2}>
            <Stack flex={1}>
              <TextFieldLabel title="Current Vehicle" />
              <CustomTextField disabled value={vehicle?.current} />
            </Stack>
            <Stack flex={1}>
              <TextFieldLabel title="New Vehicle" />
              <CustomSelectBox
                MenuProps={MenuProps}
                displayEmpty
                value={vehicle.selected}
                onChange={(e) => {
                  setVehicle((prev) => ({ ...prev, selected: e.target.value }));
                }}
              >
                <MenuItem disabled value="">
                  Select
                </MenuItem>
                {getAllTransportData?.data?.list?.map((item) => (
                  <MenuItem value={item?.vehicleNumber}>
                    {item?.vehicleNumber}
                  </MenuItem>
                ))}
              </CustomSelectBox>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={replaceVehicleLoading}
            variant="contained"
            color="secondary"
            onClick={() =>
              replaceVehicleMutate({
                routeDocId: vehicle.route,
                currentVehicleNumber: vehicle.current,
                newVehicleNumber: vehicle.selected,
              })
            }
          >
            Replace
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* View Route Detail Dialog */}
      {values.viewDialog && (
        <ViewRouteDetail
          open={values.viewDialog}
          vehicleList={getAllTransportData?.data?.list}
          routeData={values.routeData}
          onClose={() =>
            setValues((prev) => ({ ...prev, routeData: {}, viewDialog: false }))
          }
          refetch={() => {
            getAllRouteListRefetch();
            getAllTransportRefetch();
          }}
          // status={"IN_ACTIVE"}
        />
      )}

      {/* View Old Stops Dialog */}
      {values.viewOldStopDialog && (
        <ViewOldStopsDialog
          open={values.viewOldStopDialog}
          oldStopListData={values.oldStopListData}
          onClose={() =>
            setValues((prev) => ({
              ...prev,
              viewOldStopDialog: false,
              oldStopListData: {},
            }))
          }
        />
      )}

      {/* Status Change Confirmation Dialog */}
      <StatusChangeDialog
        open={values.statusChangeDialog}
        handleClose={handleCloseStatusChangeDialog}
        handleStatusChange={handleConfirmStatusChange}
        loading={updateRouteStatusLoading}
        route={values.selectedRouteForStatusChange}
      />

      <Stack
        mt={isMobile && 2}
        direction="row"
        justifyContent="flex-end"
        gap={2}
      >
        <Button
          onClick={() => setValues((prev) => ({ ...prev, createDialog: true }))}
          variant="contained"
          color="secondary"
          startIcon={<Add />}
          size={isMobile ? "small" : "large"}
        >
          Add New Route
        </Button>
        <ExportButtonGroup
          handleClickExcel={() => setShowExcelExportDialog(true)}
          showPdf={true}
          handleClickPdf={() => setShowPdfExportDialog(true)}
        />
      </Stack>

      {/* <Table columns={columns} data={getAllRouteData?.data?.list} /> */}
      <Box sx={{ mt: "1rem", marginBottom: isMobile ? 12 : 0 }}>
        {getAllRouteLoading || getAllRouteFetching ? (
          <CustomLinearProgress
            loading={getAllRouteLoading || getAllRouteFetching}
          />
        ) : null}

        <CommonTable1
          columns={columns}
          data={getAllRouteData?.data?.list}
          maxHeight={"70vh"}
        />
      </Box>
    </>
  );
};

export default RouteDetail;
